import { Credentials, Property, AuthUser, User } from '../types';

const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

// Auto-detect domain for OAuth redirect - detect actual current location
const getRedirectUri = (): string => {
  const currentUrl = window.location;
  const protocol = currentUrl.protocol;
  const hostname = currentUrl.hostname;
  const port = currentUrl.port;
  
  // Build base URL from current location
  let baseUrl = `${protocol}//${hostname}`;
  if (port && port !== '80' && port !== '443') {
    baseUrl += `:${port}`;
  }
  
  console.log('Detected current location:', baseUrl); // Debug log
  
  return `${baseUrl}/oauth-callback.html`;
};

// Get server-generated OAuth state for CSRF protection
const getServerOAuthState = async (): Promise<string> => {
  const response = await fetch('/api/oauth/state', {
    credentials: 'include' // Include cookies for secure state storage
  });
  
  if (!response.ok) {
    throw new Error('Failed to get OAuth state from server');
  }
  
  const data = await response.json();
  return data.state;
};

export const getAuthUrl = async (): Promise<string> => {
  if (!CLIENT_ID) {
    throw new Error('Google Client ID not configured');
  }

  // Get server-generated state parameter for CSRF protection
  const state = await getServerOAuthState();
  console.log('Retrieved server-generated state:', state.substring(0, 10) + '...');
  console.log('Server OAuth state retrieved for CSRF protection');

  const redirectUri = getRedirectUri();
  const scope = 'https://www.googleapis.com/auth/webmasters.readonly https://www.googleapis.com/auth/userinfo.email';
  
  const params = new URLSearchParams({
    client_id: CLIENT_ID,
    redirect_uri: redirectUri,
    scope: scope,
    response_type: 'code',
    access_type: 'offline',
    prompt: 'consent',
    state: state // Include server-generated state parameter for CSRF protection
  });

  console.log('All params:', Object.fromEntries(params.entries()));
  const finalUrl = `https://accounts.google.com/o/oauth2/auth?${params.toString()}`;
  console.log('Final OAuth URL with server state:', finalUrl);
  
  return finalUrl;
};

export const exchangeCodeForToken = async (code: string, state: string, referralCode?: string): Promise<AuthUser> => {
  const redirectUri = getRedirectUri();
  
  console.log('Calling secure backend for token exchange with server-side state validation...');
  
  const response = await fetch('/api/oauth/exchange', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      code: code,
      redirectUri: redirectUri,
      state: state, // Send state for server-side validation against HttpOnly cookie
      referralCode: referralCode // Include referral code if present
    }),
    credentials: 'include' // Include HttpOnly cookies for state validation
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(`Token exchange failed: ${error.error}`);
  }

  const authData = await response.json();
  console.log('Token exchange successful with server-side state validation');
  console.log('User data received from server:', authData.user);
  console.log('isAdmin status:', authData.user.isAdmin);
  
  // SECURITY FIX: Return user only (credentials stored securely on server)
  // State validation and cleanup handled entirely on server-side
  return {
    user: authData.user
  };
};

export const refreshToken = async (refreshToken: string): Promise<Credentials> => {
  console.log('Calling secure backend for token refresh...');
  
  const response = await fetch('/api/oauth/refresh', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      refreshToken: refreshToken,
    }),
    credentials: 'include'
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(`Token refresh failed: ${error.error}`);
  }

  const credentials = await response.json();
  console.log('Token refresh successful via backend');
  
  return credentials;
};

// Get user info by ID
export const getUserInfo = async (userId: number): Promise<User> => {
  const response = await fetch(`/api/user/${userId}`, {
    credentials: 'include'
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(`Failed to get user info: ${error.error}`);
  }
  
  return await response.json();
};

// Update user subscription status
export const updateUserSubscription = async (
  userId: number, 
  status: string, 
  stripeCustomerId?: string, 
  stripeSubscriptionId?: string
): Promise<void> => {
  const response = await fetch(`/api/user/${userId}/subscription`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      status,
      stripeCustomerId,
      stripeSubscriptionId,
    }),
    credentials: 'include'
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(`Failed to update subscription: ${error.error}`);
  }
};

// Delete user account
export const deleteUserAccount = async (userId: number): Promise<void> => {
  const response = await fetch(`/api/user/${userId}`, {
    method: 'DELETE',
    credentials: 'include'
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(`Failed to delete account: ${error.error}`);
  }
};

export const getProperties = async (): Promise<Property[]> => {
  const response = await fetch('/api/gsc/properties', {
    credentials: 'include'
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch properties: ${response.statusText}`);
  }

  return await response.json();
};

// Logout function to destroy session
export const logout = async (): Promise<void> => {
  const response = await fetch('/api/logout', {
    method: 'POST',
    credentials: 'include'
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(`Logout failed: ${error.error}`);
  }
  
  console.log('Logout successful');
};